import { createRoot, hydrateRoot } from "react-dom/client";
import App from "./App.tsx";
import "./fonts.css";
import "./index.css";
// Eagerly evaluates the lead-attribution entry snapshot (CWS-101/D11): it
// must read the entry URL at page load, and since CWS-196 the lead modals no
// longer reach it through an eager import chain. Kept structural here rather
// than incidental to the shared chunk's membership — pinned in the negative
// by tests-node/leadModalChunkGuard.test.ts.
import "./lib/leadAttribution";
import {
  initializeLanguage,
  isBackgroundLocalePreloadInFlight,
  scheduleBackgroundLocalePreload,
} from "./i18n";
import { installVitePreloadErrorRecovery } from "./lib/vitePreloadRecovery";

// The recovery's skip predicate is the background-preload flag: a locale
// prefetch failure fires the same identity-less ``vite:preloadError`` a stale
// route chunk does, and reloading for one lands on the pre-commit URL of
// whatever lazy navigation is in flight (the 2026-09-22 nightly firefox
// failure). Route-chunk failures outside that window recover as before.
installVitePreloadErrorRecovery(window, () => Date.now(), isBackgroundLocalePreloadInFlight);

async function bootstrap() {
  const language = await initializeLanguage();
  // The root API follows the document: a prerendered document ships the
  // rendered tree inside #root (CWS-57, RENDERING.md G-4), and the boot
  // hydrates it instead of discarding and re-rendering it. The element is
  // the SECOND argument, deliberately: the `hydrateRoot(container).render(...)`
  // calling shape measures as an early update before anything hydrates,
  // which switches the entire root to client rendering — the outcome the
  // dev-build measurement caught (2026-09-21, recorded in the step file)
  // and the e2e spec pins. The language half of the agreement is already
  // safe (G-3): the URL prefix wins `resolveInitialLanguage`, so a visitor
  // of a prerendered `/de/…` page boots into German, the same tree the
  // build rendered.
  //
  // The apex was the one exception (SYSTEM_DESIGN §10) until CWS-51: the
  // generator now prerenders `dist/index.html` too — and since CWS-237
  // that body is the English homepage experience (header + Index +
  // footer), no longer the language selector. Every shipped document
  // reaches the hydrate branch, with one contract that makes the apex
  // safe: `initializeLanguage` pins `en` on pathname `/` so the client
  // hydrates exactly the tree the server rendered (the CWS-232 contract);
  // the root route then resolves the visitor's real language and
  // `replace`-navigates to `/{lang}` (CWS-237). The `createRoot` fallback
  // below is purely defensive now (a document that shipped without
  // prerendered children; the 404 page never carries any). It is retained,
  // not deleted: `hydrateRoot` against an empty container throws #418 +
  // #423 to the console (the errors PageSpeed reported, CWS-232) and falls
  // back to a full client render — the page worked, the console did not.
  // `createRoot` is the correct API when there is no server markup to reuse,
  // and B-2 fails the build on any generated document whose render came
  // back empty, so a generated document cannot reach this branch silently.
  const root = document.getElementById("root")!;
  if (root.childElementCount > 0) {
    hydrateRoot(root, <App />);
  } else {
    createRoot(root).render(<App />);
  }
  // CWS-199: the four non-active locales load in the background, off the
  // load-critical path (the helper anchors to `load` + idle). Language
  // switching stays instant once they land; before that, a switch pays its
  // own ensure.
  scheduleBackgroundLocalePreload(language);
}

void bootstrap();
